import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useNetworkStore, type NetworkStatus } from '@shared/network';
import { cn } from '@shared/lib';

interface NetworkBannerProps {
  onRetry?: () => Promise<unknown> | void;
}

const RECONNECTED_DISMISS_MS = 2400;

export function NetworkBanner({ onRetry }: NetworkBannerProps) {
  const { t } = useTranslation();
  const status = useNetworkStore((s) => s.status);
  const suppressed = useNetworkStore((s) => s.bannerSuppressed);
  const setStatus = useNetworkStore((s) => s.setStatus);
  const bumpRetry = useNetworkStore((s) => s.bumpRetry);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (status !== 'reconnected') return;
    const timer = window.setTimeout(() => {
      if (useNetworkStore.getState().status === 'reconnected') {
        setStatus('online');
      }
    }, RECONNECTED_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [status, setStatus]);

  if (typeof document === 'undefined') return null;

  // Экран сам говорит о сети на месте (bannerSuppressed) — плашку не показываем; таймер выше всё равно
  // переводит «восстановлено» в online.
  const visible = status !== 'online' && !suppressed;

  const handleRetry = async () => {
    if (retrying || !onRetry) return;
    bumpRetry();
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[1600] flex justify-center">
      <AnimatePresence initial={false}>
        {visible && (
          <motion.div
            key={status}
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.24, ease: [0.2, 0.9, 0.2, 1] }}
            className="pointer-events-auto mt-3 w-[min(640px,calc(100%-24px))]"
            role="status"
            aria-live="polite"
          >
            <BannerCard status={status} retrying={retrying} onRetry={handleRetry} t={t} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
}

interface BannerCardProps {
  status: NetworkStatus;
  retrying: boolean;
  onRetry: () => void;
  t: (key: string) => string;
}

function BannerCard({ status, retrying, onRetry, t }: BannerCardProps) {
  const isReconnected = status === 'reconnected';
  const isOffline = status === 'offline';

  const titleKey = isReconnected
    ? 'network.reconnected.title'
    : isOffline
      ? 'network.offline.title'
      : 'network.serverDown.title';
  const subKey = isReconnected
    ? 'network.reconnected.sub'
    : isOffline
      ? 'network.offline.sub'
      : 'network.serverDown.sub';

  const tone = isReconnected
    ? 'border-success/35 bg-[#0f1f17]'
    : isOffline
      ? 'border-warn/35 bg-[#1f1a0b]'
      : 'border-danger/35 bg-[#1f1010]';

  return (
    <div
      className={cn(
        // На телефоне «Повторить» — строкой под текстом: справа он сжимал текст в узкую колонку.
        'flex flex-wrap items-start gap-x-3 gap-y-2.5 rounded-card border px-4 py-3.5 text-fg shadow-[0_18px_44px_-12px_rgba(0,0,0,0.7)] sm:flex-nowrap sm:px-5 sm:py-4',
        tone,
      )}
    >
      <div className="mt-0.5 shrink-0">
        <Glyph status={status} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold leading-tight tracking-[0.01em] text-fg">
          {t(titleKey)}
        </div>
        <div className="mt-1 text-[14px] leading-relaxed text-fg/70">{t(subKey)}</div>
      </div>
      {!isReconnected && (
        <>
          {/* Перенос строки только на телефоне: кнопка встаёт под текст своей ширины. */}
          <span aria-hidden className="h-0 basis-full sm:hidden" />
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className={cn(
              'ml-8 shrink-0 self-start rounded-pill border px-3.5 py-1.5 text-[12px] font-medium tracking-[0.02em] transition-colors sm:ml-0 sm:self-center',
              'border-fg/15 bg-fg/[0.04] text-fg hover:bg-fg/[0.08]',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {retrying ? (
              <span className="inline-flex items-center gap-2">
                <Spinner />
                {t('network.retrying')}
              </span>
            ) : (
              t('network.retry')
            )}
          </button>
        </>
      )}
    </div>
  );
}

function Glyph({ status }: { status: NetworkStatus }) {
  if (status === 'reconnected') {
    return (
      <svg className="h-5 w-5 text-success" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
          d="M4 10.5l4 4 8-8"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (status === 'offline') {
    return (
      <svg className="h-5 w-5 text-warn" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
          d="M3 7.5C5 5.5 7.5 4.5 10 4.5c2.5 0 5 1 7 3"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M5.5 10.5c1.4-1.3 2.9-2 4.5-2s3.1.7 4.5 2"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M8 13.5c.6-.6 1.3-.9 2-.9s1.4.3 2 .9"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="10" cy="16" r="0.9" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg className="h-5 w-5 text-danger" viewBox="0 0 20 20" fill="none" aria-hidden>
      <rect
        x="3.5"
        y="5"
        width="13"
        height="8"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="M3.5 9.5h13" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="6" cy="11.5" r="0.7" fill="currentColor" />
      <circle cx="8.5" cy="11.5" r="0.7" fill="currentColor" />
      <path
        d="M13.5 14.5l3 3M16.5 14.5l-3 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path
        d="M14 8a6 6 0 0 0-6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
